import * as React from 'react'
import { graphql, PageProps } from 'gatsby'
import {
	Helmet,
	I18nextContext,
	Link,
	useI18next,
	useTranslation,
} from 'gatsby-plugin-react-i18next'
import type { SchedulesQuery } from '@graphql-types'
import { css, useTheme } from '@emotion/react'
import { useContext, useMemo, useEffect } from 'react'
import SEO from '@components/SEO'
import Container from '@components/Container'
import moment from 'moment'
import CourseMode from '@components/CourseMode'
import { maxWidth, minWidth, useNumberFormatters } from 'src/themes'

interface AllPrice {
	courseName: string
	courseLink: string
	date: moment.Moment
	time?: string
	price: string
	priceUK?: string
	priceCA?: string
	currency?: string
	earlyBirdPrice: string
	earlyBirdDeadline: moment.Moment | null
	location: { country: string; location: string }[]
	type: 'full-time' | 'night-time' | 'workshop' | 'free'
}

const locationSet = [
	{
		country: 'hk' as const,
		location: 'kowloon_bay' as const,
	},
	{
		country: 'uk' as const,
		location: 'london' as const,
	},
	{
		country: 'uk' as const,
		location: 'uk_online' as const,
	}
]

const SchedulesPage = (props: PageProps<SchedulesQuery>) => {
	const { t } = useTranslation()
	const theme = useTheme()
	const { changeLanguage } = useI18next()
	const context = useContext(I18nextContext)
	const numberFormatters = useNumberFormatters()

	const courseMAP = props.data.course!
	const courseFLEX = props.data.allFlexCourses!
	const coursePRO = props.data.allCourses!
	const courseDates = props.data.events.edges
	const trialLessonEvent = props.data.trialLessonEvent
	const trialLessonEventUK = props.data.trialLessonEventUK

	const flexs = useMemo(() => {
		const cohorts: {
			name: string
			course: typeof courseFLEX.nodes[number]
			dates: moment.Moment[]
			time: string
			location: { country: string; location: string }[]
			show: boolean
		}[] = []
		for (const lesson of courseDates) {
			if (lesson.node.summary == null) {
				continue
			}
			const [courseCode, cohortName] = lesson.node.summary.split(' ', 2)

			let course = courseFLEX.nodes.find(
				(aCourse) => aCourse.code === courseCode
			)
			if (course == null) {
				continue
			}
			let cohort = cohorts.find(
				(aCohort) =>
					aCohort.name === cohortName && aCohort.course === course
			)
			if (cohort == null) {
				cohort = {
					name: cohortName,
					course: course,
					dates: [moment(lesson.node.start?.dateTime)],
					location: lesson.node.location
						? [{ country: 'hk', location: lesson.node.location }]
						: [],
					// endTime: moment(lesson.node.end?.dateTime).format('HH:mm'),
					time: moment(lesson.node.start?.dateTime).format('h:mm a'),
					show: moment(lesson.node.start?.dateTime).isAfter(
						new Date()
					),
				}
				cohorts.push(cohort)
			} else {
				cohort.dates.push(moment(lesson.node.start?.dateTime))
			}
		}
		return cohorts
	}, [courseDates, courseFLEX])

	const pros = useMemo(() => {
		const today = moment()
		const classes: AllPrice[] = []

		for (const pro of coursePRO.nodes) {
			const cohorts =
				pro.cohorts?.filter((val) =>
					moment(val?.value?.start_date, 'YYYY-MM-DD').isAfter(today)
				) ?? []

			for (const cohort of cohorts) {
				classes.push({
					courseName: pro.name,
					courseLink: `/courses/${pro.code}/`,
					date: moment(cohort?.value?.start_date, 'YYYY-MM-DD'),
					price: courseMAP.standard_price ?? '',
					earlyBirdPrice: cohort?.value?.price ?? '',
					earlyBirdDeadline:
						cohort?.value?.price_valid_until != null
							? moment(
									cohort?.value?.price_valid_until,
									'YYYY-MM-DD'
							  )
							: null,
					location: locationSet.filter(
						(venue) => cohort?.value?.[venue.location]
					),
					type: 'night-time' as const,
				})
			}
		}

		return classes
	}, [coursePRO])

	const courses = useMemo<AllPrice[]>(() => {
		const today = moment()
		const cohorts = courseMAP.cohorts?.filter((val) =>
			moment(val?.value?.start_date, 'YYYY-MM-DD').isAfter(today)
		)

		return [
			...pros,
			...(cohorts?.map((cohort) => ({
				courseName: courseMAP.name,
				courseLink: `/courses/MAP/`,
				date: moment(cohort?.value?.start_date, 'YYYY-MM-DD'),
				price: courseMAP.standard_price ?? '',
				priceUK: cohort?.value?.price_uk ?? undefined,
				priceCA: cohort?.value?.price_ca ?? undefined,
				time: '9:00 am',
				earlyBirdPrice: cohort?.value?.price ?? '',
				earlyBirdDeadline:
					cohort?.value?.price_valid_until != null
						? moment(cohort?.value?.price_valid_until, 'YYYY-MM-DD')
						: null,
				location: locationSet.filter(
					(venue) => cohort?.value?.[venue.location]
				),
				type: 'full-time' as const,
			})) ?? []),
			...(flexs?.map((flex) => ({
				courseName: flex.course.name ?? '',
				courseLink: `/courses/FLEX/classes/${flex.course.code ?? ''}`,
				date: moment(flex.dates[0], 'YYYY-MM-DD'),
				time: flex.time,
				price: flex.course.price_in_person_standard_price ?? '',
				earlyBirdPrice: flex.course.price_in_person ?? '',
				earlyBirdDeadline: null,
				location: flex.location,
				type: 'night-time' as const,
			})) ?? []),
			...(trialLessonEvent
				? [
						{
							courseName:
								trialLessonEvent.name?.text?.replace(
									/【.*?】/,
									''
								) ?? '',
							courseLink: trialLessonEvent.url ?? '',
							date: moment(
								trialLessonEvent.start?.utc,
								'YYYY-MM-DD'
							),
							time: moment(trialLessonEvent.start?.utc).format(
								'h:mm a'
							),
							price: '199',
							earlyBirdPrice: '199',
							earlyBirdDeadline: null,
							location: [
								{ country: 'hk', location: 'kowloon_bay' },
							],
							type: 'workshop' as const,
						},
				  ]
				: []),
			...(trialLessonEventUK
				? [
						{
							courseName:
								trialLessonEventUK.name?.text?.replace(
									/【.*?】/,
									''
								) ?? '',
							courseLink: trialLessonEventUK.url ?? '',
							date: moment(
								trialLessonEventUK.start?.utc,
								'YYYY-MM-DD'
							),
							time: moment(trialLessonEventUK.start?.utc).format(
								'h:mm a'
							),
							price: '50',
							currency: 'usd',
							earlyBirdPrice: '6.99',
							earlyBirdDeadline: null,
							location: [
								{ country: 'uk', location: 'uk_online' },
							],
							type: 'workshop' as const,
						},
				  ]
				: []),
		].sort((a, b) => {
			if (a.date.isAfter(b.date, 'day')) {
				return 1
			} else if (b.date.isAfter(a.date, 'day')) {
				return -1
			} else {
				return 0
			}
		})
	}, [courseMAP, pros, flexs, coursePRO, trialLessonEvent])

	return (
		<Container>
			<SEO title={t('schedules.title')} keywords={[]} />
			<h1>{t('schedules.title')}</h1>
			<div
				css={css`
					display: grid;
					width: 100%;
					grid-template-columns: 1fr auto 1fr 1fr;

					${maxWidth[2]} {
						grid-template-columns: 1fr 1fr;
					}

					${maxWidth[1]} {
						grid-template-columns: 1fr;
					}

					p {
						display: block;
						padding: 0.5rem;
						margin: 0;
					}
				`}>
				{courses.map((course, i) => (
					<div
						key={i}
						css={css`
							display: contents;
						`}>
						<p>
							<span
								css={css`
									font-weight: bold;
								`}>
								{course.date.format('yyyy-MM-DD (ddd)')}
							</span>
							{course.time != null && (
								<>
									<br />
									{course.time}
								</>
							)}
						</p>
						<p>
							<CourseMode mode={course.type} isInline />
							<Link
								css={css`
									text-decoration: underline;
								`}
								to={course.courseLink}>
								{course.courseName}
							</Link>
						</p>
						<p
							css={css`
								${maxWidth[2]} {
									border-bottom: solid 1px ${theme.textColor};
								}
								${maxWidth[1]} {
									border-bottom: none;
								}
							`}>
							{course.location.map((venue, j) => (
								<span
									css={css`
										display: block;
									`}
									key={i}>
									<span
										className={`fi fi-${venue.country}`}></span>{' '}
									{t(`courses:common.${venue.location}`)}{' '}
								</span>
							))}
						</p>
						<p
							css={css`
								${minWidth[2]} {
									text-align: right;
								}
								${maxWidth[2]} {
									border-bottom: solid 1px ${theme.textColor};
								}
							`}>
							{!isNaN(parseFloat(course.earlyBirdPrice)) &&
								numberFormatters[
									course.currency ?? 'hkd'
								]?.format(parseFloat(course.earlyBirdPrice))}
							{course.priceUK && (
								<>
									{numberFormatters['gbp']?.format(
										parseFloat(course.priceUK)
									)}
									<br />
								</>
							)}
							{course.priceCA && (
								<>
									{numberFormatters['cad']?.format(
										parseFloat(course.priceCA)
									)}
									<br />
								</>
							)}
							{course.earlyBirdDeadline != null &&
								course.earlyBirdPrice != course.price && (
									<>
										<br />
										<span
											css={css`
												font-size: 0.9rem;
												font-style: italic;
												color: ${theme.highlightColor};
											`}>
											{t('schedules.valid_until', {
												date: course.earlyBirdDeadline.format(
													'yyyy-MM-DD'
												),
											})}
										</span>
									</>
								)}
						</p>
					</div>
				))}
			</div>
		</Container>
	)
}

export default SchedulesPage

export const query = graphql`
	query Schedules($language: String!) {
		...TranslationFragment
		...MAPCoursePageFragment
		allCourses: allCockpitCoursesCollection(
			filter: { _lang: { eq: $language }, code: { ne: "MAP" } }
		) {
			nodes {
				_id
				code
				name
				standard_price
				cohorts {
					value {
						cohort
						price
						price_uk
						price_ca
						price_valid_until
						kowloon_bay
						kowloon_bay_full
						kowloon_bay_almost
						sheung_wan
						sheung_wan_almost
						sheung_wan_full
						start_date
						tsuen_wan
						tsuen_wan_almost
						tsuen_wan_full
						uk_online
						uk_online_almost
						uk_online_full
						london
						london_almost
						london_full
						ca_online
						ca_online_almost
						ca_online_full
						toronto
						toronto_almost
						toronto_full
					}
				}
			}
		}
		events: allGoogleCalendarEvent {
			edges {
				node {
					id
					summary
					status
					start {
						dateTime
					}
					end {
						dateTime
					}
					location
				}
			}
		}
		allFlexCourses: allCockpitFlexCoursesNewCollection(
			filter: { _lang: { eq: $language }, active: { eq: true } }
		) {
			nodes {
				_id
				code
				name
				price_in_person_standard_price
				price_in_person
			}
		}
	}
`
